import React, { useState, useEffect } from 'react';
import {
  Search,
  CalendarCheck,
  Clock,
  CheckCircle,
  XCircle,
  X,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  DollarSign
} from 'lucide-react';
import apiService from './services/api';
import { Panel, Section, StatCard, Badge, Button, LoadingSpinner } from './components';

const STATUS_BADGE = {
  pending_payment: { label: 'Pending Payment', variant: 'neutral' },
  paid:            { label: 'Paid',            variant: 'info' },
  confirmed:       { label: 'Confirmed',       variant: 'success' },
  scheduled:       { label: 'Scheduled',       variant: 'warning' },
  completed:       { label: 'Completed',       variant: 'success_strong' },
  cancelled:       { label: 'Cancelled',       variant: 'error' },
  refunded:        { label: 'Refunded',        variant: 'error' },
};

const NEXT_ACTIONS = {
  paid:      { label: 'Confirm',  nextStatus: 'confirmed' },
  confirmed: { label: 'Schedule', nextStatus: 'scheduled' },
  scheduled: { label: 'Complete', nextStatus: 'completed', confirm: true },
};

const PREV_ACTIONS = {
  confirmed: { label: 'Back to Paid',      prevStatus: 'paid' },
  scheduled: { label: 'Back to Confirmed',  prevStatus: 'confirmed' },
  completed: { label: 'Revert to Scheduled', prevStatus: 'scheduled' },
};

function OnlineBookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Detail modal state
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');

  useEffect(() => {
    loadBookings();
    loadStats();
  }, [statusFilter, searchTerm]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;

      const data = await apiService.getOnlineBookings(params);
      setBookings(data.results || []);
    } catch (err) {
      setError(err.message || 'Failed to load bookings');
      console.error('Error loading bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await apiService.getOnlineBookingStats();
      setStats(data);
    } catch (err) {
      console.error('Error loading booking stats:', err);
    }
  };

  const openDetailModal = async (booking) => {
    try {
      const detail = await apiService.getOnlineBooking(booking.id);
      setSelectedBooking(detail);
      setShowDetailModal(true);
      setUpdateError('');
    } catch (err) {
      console.error('Error loading booking detail:', err);
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedBooking(null);
    setUpdateError('');
  };

  const handleStatusUpdate = async (bookingId, newStatus, skipConfirm = false) => {
    // Confirm before completing
    if (!skipConfirm && newStatus === 'completed') {
      if (!window.confirm('Mark this booking as completed?')) return;
    }
    // Confirm before cancelling
    if (!skipConfirm && newStatus === 'cancelled') {
      if (!window.confirm('Cancel this booking? This cannot be undone.')) return;
    }

    setUpdating(true);
    setUpdateError('');

    try {
      await apiService.updateOnlineBooking(bookingId, { status: newStatus });
      loadBookings();
      loadStats();

      if (selectedBooking && selectedBooking.id === bookingId) {
        const updated = await apiService.getOnlineBooking(bookingId);
        setSelectedBooking(updated);
      }
    } catch (err) {
      setUpdateError(err.message || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '-';
    const [hours, minutes] = timeString.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return `$${parseFloat(value).toFixed(2)}`;
  };

  const filterOptions = [
    { value: 'all', label: 'All Bookings' },
    { value: 'paid', label: 'Paid' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  if (loading && bookings.length === 0) {
    return (
      <div className="flex-1 p-4 md:p-6 overflow-y-auto">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-4 md:p-6 overflow-y-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <CalendarCheck className="w-6 h-6 text-red-800" />
            Online Bookings
          </h1>
          <p className="text-sm text-gray-500 mt-1">View and manage website booking requests</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={CalendarCheck}
            value={stats.total_count || 0}
            label={`TOTAL ${formatCurrency(stats.total_revenue)}`}
          />
          <StatCard
            icon={Clock}
            value={stats.paid_count || 0}
            label={`AWAITING ${formatCurrency(stats.paid_value)}`}
            onClick={() => setStatusFilter('paid')}
          />
          <StatCard
            icon={CheckCircle}
            value={stats.confirmed_count || 0}
            label={`CONFIRMED ${formatCurrency(stats.confirmed_value)}`}
            onClick={() => setStatusFilter('confirmed')}
          />
          <StatCard
            icon={DollarSign}
            value={stats.completed_count || 0}
            label={`COMPLETED ${formatCurrency(stats.completed_value)}`}
            onClick={() => setStatusFilter('completed')}
          />
        </div>
      )}

      {/* Search and Filters */}
      <Panel className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by client name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
          >
            {filterOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </Panel>

      {/* Bookings Table */}
      <Panel noPadding>
        {error ? (
          <div className="p-8 text-center text-red-500">{error}</div>
        ) : bookings.length === 0 ? (
          <div className="p-8 text-center">
            <CalendarCheck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No bookings found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Services
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Requested Date
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paid At
                  </th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {bookings.map((booking) => {
                  const nextAction = NEXT_ACTIONS[booking.status];
                  const badge = STATUS_BADGE[booking.status] || STATUS_BADGE.pending_payment;
                  return (
                    <tr
                      key={booking.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => openDetailModal(booking)}
                    >
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-900">{booking.client_name}</div>
                        <div className="text-xs text-gray-500">{booking.client_phone}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900 max-w-[200px] truncate" title={booking.service_names}>
                          {booking.service_names || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{formatDate(booking.requested_date)}</div>
                        <div className="text-xs text-gray-500">{formatTime(booking.requested_time)}</div>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <span className="text-sm font-semibold text-gray-900">{formatCurrency(booking.subtotal)}</span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <Badge variant={badge.variant} small>{badge.label}</Badge>
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-500 whitespace-nowrap">
                        {formatDateTime(booking.paid_at)}
                      </td>
                      <td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {PREV_ACTIONS[booking.status] && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStatusUpdate(booking.id, PREV_ACTIONS[booking.status].prevStatus, true)}
                              disabled={updating}
                              className="text-gray-500 hover:bg-gray-100"
                              title={PREV_ACTIONS[booking.status].label}
                            >
                              ↩
                            </Button>
                          )}
                          {nextAction && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleStatusUpdate(booking.id, nextAction.nextStatus)}
                              disabled={updating}
                            >
                              {nextAction.label}
                            </Button>
                          )}
                          {['paid', 'confirmed', 'scheduled'].includes(booking.status) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStatusUpdate(booking.id, 'cancelled')}
                              disabled={updating}
                              className="text-red-700 hover:bg-red-50"
                            >
                              Cancel
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Detail Modal */}
      {showDetailModal && selectedBooking && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={closeDetailModal}
          />

          <div className="fixed inset-0 z-50 flex items-center justify-center p-2">
            <div className="bg-white border border-gray-200 shadow-lg max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="bg-red-800 text-white px-3 py-2 flex items-center justify-between sticky top-0 z-10 border-b border-red-900">
                <h2 className="text-sm font-semibold">Booking Details</h2>
                <button
                  onClick={closeDetailModal}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Error Display */}
              {updateError && (
                <div className="mx-2 mt-2 bg-red-50 border border-red-200 p-2">
                  <p className="text-red-800 text-xs">{updateError}</p>
                </div>
              )}

              {/* Content */}
              <div className="px-3 py-2 space-y-2">
                {/* Status + Amount */}
                <div className="flex items-center justify-between">
                  <Badge variant={(STATUS_BADGE[selectedBooking.status] || STATUS_BADGE.pending_payment).variant} small>
                    {(STATUS_BADGE[selectedBooking.status] || STATUS_BADGE.pending_payment).label}
                  </Badge>
                  <span className="text-xs text-gray-500">ID: {selectedBooking.id}</span>
                </div>

                {/* Booking Summary */}
                <div className="border border-gray-200 p-2 bg-white">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-lg font-semibold text-gray-900">{formatCurrency(selectedBooking.subtotal)}</span>
                    <span className="text-xs text-gray-500">
                      {formatDate(selectedBooking.requested_date)} at {formatTime(selectedBooking.requested_time)}
                    </span>
                  </div>
                </div>

                {/* Client Information */}
                <div className="border border-gray-200 p-2">
                  <h3 className="text-xs font-medium text-gray-900 mb-2 flex items-center uppercase">
                    <User className="w-3.5 h-3.5 mr-1" />
                    Client Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                    <div>
                      <label className="text-gray-500">Name</label>
                      <p className="text-gray-900 font-medium">{selectedBooking.client_name}</p>
                    </div>
                    <div>
                      <label className="text-gray-500 flex items-center">
                        <Phone className="w-3 h-3 mr-1" />
                        Phone
                      </label>
                      <p className="text-gray-900">{selectedBooking.client_phone}</p>
                    </div>
                    <div>
                      <label className="text-gray-500 flex items-center">
                        <Mail className="w-3 h-3 mr-1" />
                        Email
                      </label>
                      <p className="text-gray-900">{selectedBooking.client_email}</p>
                    </div>
                    <div>
                      <label className="text-gray-500 flex items-center">
                        <MapPin className="w-3 h-3 mr-1" />
                        Address
                      </label>
                      <p className="text-gray-900">
                        {selectedBooking.address}, {selectedBooking.city}, {selectedBooking.state} {selectedBooking.zip_code}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Services */}
                <div className="border border-gray-200 p-2">
                  <h3 className="text-xs font-medium text-gray-900 mb-2 uppercase">Services</h3>
                  <div className="space-y-1">
                    {selectedBooking.booking_services?.map((s) => (
                      <div key={s.id} className="flex justify-between text-xs">
                        <span className="text-gray-700">{s.service_name}</span>
                        <span className="font-medium text-gray-900">{formatCurrency(s.price)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between pt-1 mt-1 border-t border-gray-200 text-xs font-semibold">
                      <span>Total</span>
                      <span>{formatCurrency(selectedBooking.subtotal)}</span>
                    </div>
                  </div>
                </div>

                {/* Special Requests */}
                {selectedBooking.special_requests && (
                  <div className="border border-gray-200 p-2">
                    <h3 className="text-xs font-medium text-gray-900 mb-2 flex items-center uppercase">
                      <FileText className="w-3.5 h-3.5 mr-1" />
                      Special Requests
                    </h3>
                    <p className="text-xs text-gray-700 whitespace-pre-wrap">{selectedBooking.special_requests}</p>
                  </div>
                )}

                {/* Timestamps */}
                <div className="text-xs text-gray-500 flex items-center justify-between border-t border-gray-200 pt-2">
                  <span>Created: {formatDateTime(selectedBooking.created_at)}</span>
                  <span>
                    {selectedBooking.paid_at ? `Paid: ${formatDateTime(selectedBooking.paid_at)}` : ''}
                    {selectedBooking.confirmation_email_sent ? ' · Email sent' : ''}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="bg-gray-50 px-3 py-2 flex flex-wrap gap-1 border-t border-gray-200 sticky bottom-0">
                {PREV_ACTIONS[selectedBooking.status] && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleStatusUpdate(selectedBooking.id, PREV_ACTIONS[selectedBooking.status].prevStatus, true)}
                    disabled={updating}
                    className="text-gray-600 hover:bg-gray-200"
                  >
                    ↩ {PREV_ACTIONS[selectedBooking.status].label}
                  </Button>
                )}
                {NEXT_ACTIONS[selectedBooking.status] && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleStatusUpdate(selectedBooking.id, NEXT_ACTIONS[selectedBooking.status].nextStatus)}
                    disabled={updating}
                  >
                    <CheckCircle className="w-3.5 h-3.5 mr-1" />
                    {updating ? 'Updating...' : NEXT_ACTIONS[selectedBooking.status].label}
                  </Button>
                )}
                {['paid', 'confirmed', 'scheduled'].includes(selectedBooking.status) && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleStatusUpdate(selectedBooking.id, 'cancelled')}
                    disabled={updating}
                  >
                    <XCircle className="w-3.5 h-3.5 mr-1" />
                    Cancel Booking
                  </Button>
                )}
                <button
                  onClick={closeDetailModal}
                  className="flex items-center px-2 py-1 text-xs bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 transition-colors ml-auto"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default OnlineBookingsPage;
